import React, { useEffect } from "react"

import useDeviceDetection from "../../hooks/useDeviceDetection"
import useWindowSize from "../../hooks/useWindowSize"

import FooterSection from "./components/FooterSection"
import HeaderSection from "./components/HeaderSection"
import SectionRenderer from "./SectionRenderer"

const WebsiteBuilderV2PublicPage = ({
  practice,
  page,
  sections,
  theme,
  viewingInBuilder = false,
  deviceType: serverDeviceType = "desktop"
}) => {
  // Use both device detection methods for maximum reliability
  // matchMedia-based detection (industry standard, matches CSS breakpoints exactly)
  const { isMobile: isMobileDeviceDetection, width: deviceDetectionWidth } = useDeviceDetection(768)
  // Window size-based detection (responds to window resizing)
  const { isMobile: isMobileWindowSize, width: windowSizeWidth } = useWindowSize(768)

  // Determine device type with preference for mobile when methods disagree
  // If deviceDetection says desktop but windowSize says mobile, prefer mobile
  const determineDeviceType = () => {
    // Use windowSize width if available, otherwise deviceDetection width
    const width = windowSizeWidth !== null && windowSizeWidth !== undefined ? windowSizeWidth : deviceDetectionWidth

    if (width === null || width === undefined) {
      return serverDeviceType
    }

    // If both agree, use that
    if (isMobileDeviceDetection === isMobileWindowSize) {
      return isMobileDeviceDetection ? "mobile" : "desktop"
    }

    // If they disagree and windowSize says mobile, prefer mobile
    if (isMobileWindowSize) {
      return "mobile"
    }

    // If they disagree and windowSize says desktop, but deviceDetection says mobile, prefer mobile
    if (isMobileDeviceDetection) {
      return "mobile"
    }

    // Fallback (shouldn't reach here, but just in case)
    return "desktop"
  }

  const deviceType = determineDeviceType()

  // Handle anchor scrolling when page loads
  useEffect(() => {
    const handleAnchorScroll = () => {
      const hash = window.location.hash
      if (hash) {
        // Remove the # from the hash
        const elementId = hash.substring(1)
        const element = document.getElementById(elementId)

        if (element) {
          // Add a small delay to ensure the page is fully rendered
          setTimeout(() => {
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            })
          }, 500) // 500ms delay
        }
      }
    }

    // Run on initial load
    handleAnchorScroll()

    // Also listen for hash changes (in case user navigates with anchors)
    window.addEventListener("hashchange", handleAnchorScroll)

    return () => {
      window.removeEventListener("hashchange", handleAnchorScroll)
    }
  }, [])

  // Handle missing or empty sections
  if (!sections || !Array.isArray(sections) || sections.length === 0) {
    return (
      <div className="website-builder-v2-public py-20 text-center">
        <h2 className="mb-4 text-2xl font-bold text-gray-800">Welcome to {practice?.name || "this practice"}</h2>
        <p className="text-gray-600">This page is being set up. Please check back soon!</p>
        <div className="mt-4 text-sm text-gray-500">
          Debug: No sections found for page &quot;{page?.title || "Unknown"}&quot;
        </div>
      </div>
    )
  }

  // Check if the first section is a HeroSection or SubHeroSection with full_image layout
  const firstSection = sections.find((section) => !section.hidden)
  const shouldHideHeader =
    (firstSection?.layout === "hero" || firstSection?.layout === "sub_hero") &&
    firstSection?.settings?.layout === "full_image"

  return (
    <div className="website-builder-v2-public">
      {/* Header - Hide if first section is HeroSection or SubHeroSection with full_image layout */}
      {!shouldHideHeader && (
        <HeaderSection
          section={{ id: "header", settings: {} }}
          practice={practice}
          theme={theme}
          highlightedField={null}
          deviceType={deviceType}
        />
      )}

      {/* Render all visible sections using the SectionRenderer */}
      {sections
        .filter((section) => !section.hidden)
        .map((section) => (
          <SectionRenderer
            key={section.id}
            section={section}
            practice={practice}
            theme={theme}
            highlightedField={null}
            viewingInBuilder={viewingInBuilder}
            pageSlug={page?.slug}
            defaultPageType={page?.defaultPageType}
            deviceType={deviceType}
            isCustom={page?.isCustom}
          />
        ))}

      {/* Footer */}
      <FooterSection
        section={{ id: "footer", settings: {} }}
        practice={practice}
        theme={theme}
        highlightedField={null}
        deviceType={deviceType}
      />
    </div>
  )
}

export default WebsiteBuilderV2PublicPage
